import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Trash2, User, CalendarX, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const DAY = 86400000;
const ymd = (iso) => (iso ? new Date(iso).toLocaleDateString("sv-SE") : "—");
export const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / DAY));
const TABS = ["all", "mine", "soon"];

const dayWord = (n, t) => {
    const m10 = n % 10, m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return t("trash_day_one");
    if (m10 >= 2 && (m100 < 11 || m100 > 19)) return t("trash_day_few");
    return t("trash_day_many");
};

const Field = ({ icon: Icon, label, value, testid, cls = "text-slate-200" }) => (
    <div className="flex items-center gap-1.5 text-xs">
        <Icon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        <span className="text-slate-500">{label}</span>
        <span data-testid={testid} className={`font-mono ${cls}`}>{value}</span>
    </div>
);

function TrashCard({ p, t, isAdmin, onRestore, onPurge, busy }) {
    const left = daysLeft(p.purge_at);
    return (
        <div data-testid={`trash-card-${p.id}`} className="bg-[#111827] border border-[#1e293b] rounded-xl p-4 flex flex-col gap-3">
            <div className="min-w-0">
                <div data-testid={`trash-name-${p.id}`} className="font-mono font-semibold truncate">{p.name}</div>
                <div data-testid={`trash-client-${p.id}`} className="text-sm text-slate-400 truncate">{p.client || t("trash_no_client")}</div>
            </div>
            <div className="space-y-1.5">
                <Field icon={CalendarX} label={t("trash_deleted_on")} value={ymd(p.deleted_at)} testid={`trash-deleted-at-${p.id}`} />
                <Field icon={CalendarX} label={t("trash_purge_on")} value={ymd(p.purge_at)} testid={`trash-purge-at-${p.id}`} />
                <Field icon={Clock} label={t("trash_left")} value={`${left} ${dayWord(left, t)}`} testid={`trash-days-left-${p.id}`}
                    cls={left <= 7 ? "text-rose-400 font-semibold" : "text-amber-300"} />
                <Field icon={User} label={t("trash_deleted_by")} value={p.deleted_by_name || "—"} testid={`trash-deleted-by-${p.id}`} />
            </div>
            <div className="flex gap-2 mt-auto pt-1">
                <Button data-testid={`trash-restore-${p.id}`} size="sm" disabled={busy} onClick={() => onRestore(p)} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> {t("trash_restore")}
                </Button>
                {isAdmin && (
                    <Button data-testid={`trash-purge-${p.id}`} size="sm" variant="outline" disabled={busy} onClick={() => onPurge(p)}
                        className="border-rose-700/60 bg-transparent text-rose-300 hover:bg-rose-950/50 hover:text-rose-200">
                        <Trash2 className="w-4 h-4 mr-1.5" /> {t("trash_purge")}
                    </Button>
                )}
            </div>
        </div>
    );
}

export function TrashView({ t, user, onChanged }) {
    const [items, setItems] = useState(null);
    const [tab, setTab] = useState("all");
    const [purgeFor, setPurgeFor] = useState(null);
    const [busy, setBusy] = useState(false);
    const isAdmin = user?.role === "admin";
    const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
    const load = async () => {
        try { const { data } = await api.get("/trash"); setItems(data); } catch (e) { err(e); setItems([]); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const act = async (fn, msg) => {
        setBusy(true);
        try { await fn(); toast.success(msg); await load(); onChanged(); } catch (e) { err(e); } finally { setBusy(false); }
    };
    const restore = (p) => act(() => api.post(`/trash/${p.id}/restore`), t("trash_restored", { name: p.name }));
    const purge = (p) => act(() => api.delete(`/trash/${p.id}`), t("trash_purged", { name: p.name }));

    const list = (items || []).filter((p) => (tab === "mine" ? p.deleted_by === user?.user_id : tab === "soon" ? daysLeft(p.purge_at) <= 7 : true));

    return (
        <section data-testid="trash-view" className="flex-1 min-w-0">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-mono font-bold text-xl flex items-center gap-2"><Trash2 className="w-5 h-5 text-rose-400" /> {t("trash_title")}</h2>
                    <p className="text-sm text-slate-400 mt-1">{t("trash_sub")}</p>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                    {TABS.map((k) => (
                        <button key={k} data-testid={`trash-filter-${k}`} onClick={() => setTab(k)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${tab === k ? "bg-rose-600 border-rose-500 text-white" : "border-[#1e293b] text-slate-300 hover:bg-[#182232]"}`}>{t(`trash_flt_${k}`)}</button>
                    ))}
                </div>
            </div>
            <div className="mt-5">
                {items === null ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-rose-400 animate-spin" /></div>
                ) : list.length === 0 ? (
                    <div data-testid="trash-empty" className="border border-dashed border-[#1e293b] rounded-2xl py-16 text-center text-slate-400">{t("trash_empty")}</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                        {list.map((p) => <TrashCard key={p.id} p={p} t={t} isAdmin={isAdmin} busy={busy} onRestore={restore} onPurge={setPurgeFor} />)}
                    </div>
                )}
            </div>
            <AlertDialog open={!!purgeFor} onOpenChange={(o) => { if (!o) setPurgeFor(null); }}>
                <AlertDialogContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("trash_purge_title", { name: purgeFor?.name })}</AlertDialogTitle>
                        <AlertDialogDescription data-testid="trash-purge-warning" className="text-rose-300">{t("trash_purge_warn")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="trash-purge-cancel" className="bg-[#182232] border-[#1e293b] text-slate-200 hover:bg-[#1e293b]">{t("dash_cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid="trash-purge-confirm" onClick={() => purge(purgeFor)} className="bg-rose-600 hover:bg-rose-500 text-white">{t("trash_purge")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
